import React from "react";
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Add, ArrowDropDown, ArrowDropUp, Delete, Edit, PeopleAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  IconContainer,
  SpaceBetweenStack,
  StyledStack,
} from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { ExtraPaddingLeftStyledCell, ListingCellCenter, ListingCellLeft, ListingCellRight, ListingHeaderContent, ListingIconMetric, StyledTableCell } from "../../../components/commonComponents/TabularLists/styles";
import AddUser from "../modalContent/AddUser";
import { loadMoreData, SortIcon } from "../../../api_calls/utils";
import { checkUserDetailsFromCookie, checkUserPermissions, deleteUser, fetchRoles, manageUsersPaginationCookie, resetUserData } from "../../../api_calls/userManagement";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { toast } from "react-toastify";
import { CustomTextInput } from "../../../components/commonComponents/TextInputs/style";
import Pagination from "../../../components/commonComponents/LoadMore/Pagination";
import { createCookie, deleteCookie } from "../../../api_calls/requestConfig";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";

const UserList = ({ usedFor }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalDetails, setModalDetails] = React.useState({});
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState({});
  const [searchVal, setSearchVal] = React.useState({ value: "" });
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;

  const checkUserDetails = checkUserDetailsFromCookie();
  const companyAllowedUserTypes = ["cyreen", "branch"];

  const currentStateData = useSelector((state) => state.userSelections);

  // sorting measures for files listing
  const [sortingMeasure, setSortingMeasure] = React.useState({
    username: "asc",
    email: "asc",
    company: "asc",
  });

  const [sortingParamName, setSortingParamName] = React.useState("username");

  const paginationStartValue =
    typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_start : 0;
  const paginationEndValue =
    typeof currentStateData.users_pagination !== "undefined" ? currentStateData.users_pagination.user_list_end : 10;

  const userRecords =
    usedFor === "cyreen"
      ? typeof currentStateData.cyreen_users !== "undefined" && currentStateData.cyreen_users.length !== 0
        ? currentStateData.cyreen_users
        : []
      : typeof currentStateData.users_list !== "undefined" && currentStateData.users_list.length !== 0
        ? currentStateData.users_list
        : [];

  // to handle loading state for the data table listing.
  const userRecordsLoading = usedFor === "cyreen"
    ? typeof currentStateData.cyreen_users === "undefined" && userRecords.length === 0
      ? true
      : false
    : typeof currentStateData.users_list === "undefined" && userRecords.length === 0
      ? true
      : false;

  const applyFilter = () => {
    if (userRecords.length !== 0) {
      if (usedFor === "cyreen") {
        createCookie("cyreen_users_filter", JSON.stringify(searchVal.value));
      } else {
        createCookie("users_filter", JSON.stringify(searchVal.value));
      }

      return sortedResults(
        userRecords.filter((user) => {
          return (
            user.username.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
            user.email.toLowerCase().toString().includes(searchVal.value.toLowerCase().toString()) ||
            user.assigned_company.name.toLowerCase().includes(searchVal.value.toLowerCase().toString()) ||
            user.roles.join(",").toLowerCase().includes(searchVal.value.toLowerCase().toString())
          );
        })
      );
    } else {
      return [];
    }
  };

  // to return the sorted list items
  const sortedResults = (toBeSortedList) => {
    return toBeSortedList.slice().sort((a, b) => {
      if (sortingParamName === "username") {
        return sortingMeasure.username === "asc"
          ? a.username.localeCompare(b.username)
          : b.username.localeCompare(a.username);
      } else if (sortingParamName === "email") {
        return sortingMeasure.email === "asc" ? a.email.localeCompare(b.email) : b.email.localeCompare(a.email);
      } else if (sortingParamName === "company") {
        return sortingMeasure.company === "asc"
          ? a.assigned_company.name.localeCompare(b.assigned_company.name)
          : b.assigned_company.name.localeCompare(a.assigned_company.name);
      } else {
        return sortingMeasure.username === "asc"
          ? a.username.localeCompare(b.username)
          : b.username.localeCompare(a.username);
      }
    });
  };

  let listItems = typeof searchVal.value !== "undefined" && searchVal.value !== "" ? applyFilter() : userRecords;

  let userListitems =
    listItems.length !== 0 ? sortedResults(listItems).slice(paginationStartValue, paginationEndValue) : [];

  const closeModal = () => {
    setModalDetails({});
    setIsModalOpen(false);
    resetUserData();
  };

  const openModal = (modalFor, userData) => {
    setModalDetails({
      openFor: modalFor,
      user_data: userData,
    });
    setIsModalOpen(true);
  };

  const editAction = (user) => {
    return (
      <HtmlTooltip title={<Typography variant="body4">Edit</Typography>}>
        <a
          href="javacript:void(0)"
          aria-label={"edit user for " + user.username}
          onClick={(e) => {
            e.preventDefault();
            openModal("update", user);
          }}
        >
          <Edit sx={{ color: "#AF3241" }} />
        </a>
      </HtmlTooltip>
    );
  };

  const deleteAction = (user) => {
    return (
      <HtmlTooltip title={<Typography variant="body4">Delete</Typography>}>
        <a
          href="javacript:void(0)"
          aria-label={"delete user for " + user.username}
          onClick={(e) => {
            e.preventDefault();

            setShowConfirmation(true);
            setUserToDelete(user);
          }}
        >
          <Delete sx={{ color: "#AF3241" }} />
        </a>
      </HtmlTooltip>
    );
  };

  const disabledEditAction = (user) => {
    return (
      <HtmlTooltipDisabled title={"No Access"}>
        <a
          href="javacript:void(0)"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <Edit sx={{ color: "#E6E6E6" }} />
        </a>
      </HtmlTooltipDisabled>
    );
  };

  const disabledDeleteAction = (user) => {
    return (
      <HtmlTooltipDisabled title={"No Access"}>
        <a
          href="javacript:void(0)"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <Delete sx={{ color: "#E6E6E6" }} />
        </a>
      </HtmlTooltipDisabled>
    );
  };

  const handleSortingParamChange = (paramName) => {
    setSortingMeasure((prevState) => {
      return {
        ...prevState,
        [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
      };
    });
    setSortingParamName(paramName);
  };

  const columns = React.useMemo(() => {
    let cols = [
      {
        id: "username",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("username")} style={{ cursor: "pointer" }}>
            Username <SortIcon direction={sortingMeasure.username} />
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            {user.username}
          </ListingCellLeft>
        ),
      },
      {
        id: "email",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("email")} style={{ cursor: "pointer" }}>
            E-mail <SortIcon direction={sortingMeasure.email} />
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            {user.email}
          </ListingCellLeft>
        ),
      }
    ];

    if (companyAllowedUserTypes.includes(checkUserDetails.user_detail.user_type)) {
      cols.push({
        id: "company",
        header: (
          <ListingHeaderContent onClick={() => handleSortingParamChange("company")} style={{ cursor: "pointer" }}>
            Company <SortIcon direction={sortingMeasure.company} />
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellLeft>
            {user.user_type === "cyreen" ? "Cyreen" : user.assigned_company.name}
          </ListingCellLeft>
        ),
      });
    }

    cols = [
      ...cols,
      {
        id: "roles",
        headerAlign: "center",
        align: "center",
        header: (
          <ListingHeaderContent>
            Role
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellCenter>
            <HtmlTooltip
              title={
                <Box width={200}>
                  <ul>
                    {user.roles.map((roleName) => {
                      return (
                        <li key={roleName} style={{ marginTop: "5px" }}>
                          {roleName}
                        </li>
                      );
                    })}
                  </ul>
                </Box>
              }
            >
              <PeopleAlt sx={{ color: "#AF3241" }} />
            </HtmlTooltip>
          </ListingCellCenter>

        ),
      },
      {
        id: "actions",
        headerAlign: "right",
        align: "right",
        header: (
          <ListingHeaderContent>
            Action
          </ListingHeaderContent>
        ),
        render: (user) => (
          <ListingCellRight>
            {/* ---- edit/delete actions if allowed ------ */}
            {usedFor === "cyreen" ? (
              <>
                {/* ----- edit ---- */}
                {permissionScope.user_list_can_edit_cyreen === true && typeof currentStateData.role_name_arr !== "undefined" && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                  ? editAction(user)
                  : disabledEditAction(user)}
                {/* ----- delete ----- */}
                {permissionScope.user_list_can_delete_cyreen === true && typeof currentStateData.role_name_arr !== "undefined" && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                  ? deleteAction(user)
                  : disabledDeleteAction(user)}
              </>
            ) : (
              <>
                {user.user_type.toLowerCase() === "brand" ? (
                  <>
                    {/* ----- edit ---- */}
                    {permissionScope.user_list_can_edit_brand === true && typeof currentStateData.role_name_arr !== "undefined" && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                      ? editAction(user)
                      : disabledEditAction(user)}
                    {/* ----- delete ----- */}
                    {permissionScope.user_list_can_delete_brand === true && typeof currentStateData.role_name_arr !== "undefined" && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                      ? deleteAction(user)
                      : disabledDeleteAction(user)}
                  </>
                ) : (
                  <>
                    {/* ----- edit ---- */}
                    {permissionScope.user_list_can_edit_external === true && typeof currentStateData.role_name_arr !== "undefined" && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                      ? editAction(user)
                      : disabledEditAction(user)}
                    {/* ----- delete ----- */}
                    {permissionScope.user_list_can_delete_external === true && typeof currentStateData.role_name_arr !== "undefined" && user.roles.every(role => currentStateData.role_name_arr.includes(role)) === true
                      ? deleteAction(user)
                      : disabledDeleteAction(user)}
                  </>
                )}
              </>
            )}
          </ListingCellRight>
        ),
      }
    ]

    return cols;
  }, [sortingMeasure, currentStateData?.role_name_arr]);

  React.useEffect(() => {
    let filterCookieKeyword = usedFor === "cyreen" ? "cyreen_users_filter" : "users_filter";

    // check if filter cookie is set and set it in redux state
    if (typeof document.cookie.split("; ").find((row) => row.startsWith(filterCookieKeyword)) !== "undefined") {
      const filterObj = JSON.parse(
        document.cookie
          .split("; ")
          .find((row) => row.startsWith(filterCookieKeyword))
          .split("=")[1]
      );
      setSearchVal({ value: filterObj });
    }

    // reset pagination redux state
    manageUsersPaginationCookie({
      user_list_start: 0,
      user_list_end: 10,
    });

  }, [usedFor]);

  return (
    <>
      {/* ============ Action buttons =========== */}
      <Stack direction="row" spacing={2}>
        {usedFor !== "cyreen" &&
          (permissionScope.user_list_can_edit_brand === true || permissionScope.user_list_can_edit_external === true) ? (
          <ActionButton
            onClick={(e) => {
              e.preventDefault();
              openModal("add", {});
            }}
            label={"New User"}
            icon={<Add />}
          />
        ) : usedFor === "cyreen" && permissionScope.user_list_can_edit_cyreen === true ? (
          <ActionButton
            onClick={(e) => {
              e.preventDefault();
              openModal("add", {});
            }}
            label={"New Cyreen User"}
            icon={<Add />}
          />
        ) : (
          <></>
        )}
      </Stack>
      <Box height={30} />
      <Box width={350}>
        <CustomTextInput
          fullWidth
          variant="standard"
          placeholder="Search"
          label="Search"
          autoComplete="off"
          type={"text"}
          value={searchVal.value}
          onChange={(e) => {
            setSearchVal({ value: e.target.value });
            if (e.target.value === "") {
              if (usedFor === "cyreen") {
                deleteCookie("cyreen_users_filter", true);
              } else {
                deleteCookie("users_filter", true);
              }

            }
          }}
          onBlur={(e) => { }}
        />
      </Box>

      {/* ============== users listing ============ */}
      <Box height={30} />
      <ListingDataTable
        columns={columns}
        rows={userListitems}
        rowKey={(user) => `${user.id}-${user.workspace_id}`}
        emptyText="No users found"
        loading={userRecordsLoading}
      />
      {/* --------- record count info and pagination --------- */}
      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mt={2} mb={5}>
        <Pagination
          totalCount={listItems.length}
          reduxPaginationKeyword="users_pagination"
          reduxStartKeyword="user_list_start"
          reduxEndKeyword="user_list_end"
          useWindowScroll={true}
        />
      </Stack>


      {/* ============ modal popup for add/update User ========== */}
      {isModalOpen && (
        <AddUser
          open={isModalOpen}
          onClose={closeModal}
          size={"md"}
          closeModal={closeModal}
          usedFor={modalDetails.openFor}
          userInfo={modalDetails.user_data}
          cyreenBoolean={usedFor === "cyreen" ? true : false}
        />
      )}
      {/* ---------- delete Confirmation ---------- */}
      {showConfirmation === true && (
        <Confirmation
          confirmationActionCall={() => {
            deleteUser(userToDelete).then((info) => {
              if (info.success === true) {
                setUserToDelete({});
                setShowConfirmation(false);
                toast("User deleted successfully ", { type: "success" });
              } else {
                toast("Sorry, something went wrong", { type: "error" });
              }
            });
          }}
          closePopup={(e) => {
            setUserToDelete({});
            setShowConfirmation(false);
          }}
          showConfirmationPopup={showConfirmation}
          confirmationTitle={"Delete User: " + userToDelete.username}
          confirmationMessage={
            <>
              <Typography variant="body2">
                You are deleting a user account. This action is permanent and cannot be reversed.
              </Typography>
              <Box height={20} />
              <Typography variant="body2">Are you sure you want to proceed?</Typography>
            </>
          }
        />
      )}
    </>
  );
};

export default UserList;
